import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { colors, fontFamilyMono } from "../../data/tokens";
import { displayDateOnly } from "../../util/dateOnly";

// Tipos de cargo como gravados no banco (enum UserPositionRoleTypes) →
// rótulo exibido. Compartilhado pela lista, ficha e formulários.
export const ROLE_TYPE_CHOICES = [
  { id: "DTE", name: "Divisão Técnica" },
  { id: "CARGOS_FUNCOES", name: "Cargos e Funções" },
  { id: "COMISSAO_EXTERNA", name: "Comissão/Externa" },
  { id: "REPRESENTACAO_EXTERNA", name: "Bancada do CREA" },
];

export const SPHERE_CHOICES = [
  { id: "federal", name: "Federal" },
  { id: "estadual", name: "Estadual" },
  { id: "municipal", name: "Municipal" },
  { id: "civil", name: "Civil" },
];

const labelFrom = (
  choices: { id: string; name: string }[],
  value?: string | null
) => choices.find((c) => c.id === value)?.name || value || "";

export const roleTypeLabel = (roleType?: string | null) =>
  labelFrom(ROLE_TYPE_CHOICES, roleType);

export const sphereLabel = (sphere?: string | null) =>
  labelFrom(SPHERE_CHOICES, sphere);

// Datas exatas do mandato em texto curto — mesmo formato da aba Cargos
// e Funções da ficha do sócio (sem os prefixos "De/Até" que poluíam)
export const roleDatesText = (fromDate?: any, toDate?: any) => {
  const from = displayDateOnly(fromDate);
  const to = displayDateOnly(toDate);
  if (from && to) return `${from} – ${to}`;
  if (from) return `desde ${from}`;
  if (to) return `até ${to}`;
  return "";
};

// Até 16/10/2026 a API convertia toda data recebida com new Date(valor) —
// e new Date(null) é 01/01/1970: salvar a edição de um cargo sem datas
// gravava 1970 (TODO.md, item 26.2, corrigido no servidor). Por cautela,
// a data vazia só vai no corpo quando o usuário APAGOU uma data que
// existia (para limpá-la); se já estava vazia, não é enviada.
export const omitEmptyDates = (
  data: any,
  options?: { previousData?: any }
) => {
  const previous = options?.previousData || {};
  const out = { ...data };
  ["fromDate", "toDate"].forEach((field) => {
    if (!out[field] && !previous[field]) delete out[field];
  });
  return out;
};

export const mutedTextSx = { fontSize: 12, color: colors.textMuted };

// Faixa de anos em destaque; as datas exatas, quando houver, embaixo
export const RolePeriod = ({ record }: { record: any }) => {
  const dates = roleDatesText(record?.fromDate, record?.toDate);
  if (!record?.yearRange && !dates) {
    return <Typography sx={mutedTextSx}>—</Typography>;
  }
  return (
    <Box>
      {record.yearRange && (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            fontFamily: fontFamilyMono,
            whiteSpace: "nowrap",
            color: colors.textStrong,
          }}
        >
          {record.yearRange}
        </Typography>
      )}
      {dates && (
        <Typography sx={{ ...mutedTextSx, whiteSpace: "nowrap" }}>
          {dates}
        </Typography>
      )}
    </Box>
  );
};

// Nomes dos sócios com link para a ficha de cada um. O clique não
// dispara o rowClick da linha (que abriria a ficha do cargo).
export const MemberLinks = ({ members }: { members?: any[] }) => {
  const list = (members || []).filter(Boolean);
  if (list.length === 0) {
    return <Typography sx={mutedTextSx}>—</Typography>;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {list.map((member: any) => (
        <Link
          key={member.id}
          href={`#/users/crud/${member.id}/show`}
          underline="hover"
          onClick={(e) => e.stopPropagation()}
          sx={{ fontSize: 13, fontWeight: 600, color: colors.primaryDark }}
        >
          {member.fullName || member.email || "Sócio sem nome"}
        </Link>
      ))}
    </Box>
  );
};
